<template>
    <div class="space-y-6">
        <!-- 1. Objective -->
        <Card>
            <CardHeader>
                <CardTitle class="text-xl"
                    ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">1</Badge>Objective / Goal</CardTitle
                >
                <CardDescription>Define why is this transform needed, what problem it solves, and its expected outcome.</CardDescription>
            </CardHeader>
            <CardContent class="pt-0">
                <div v-if="loadingPage" class="space-y-3">
                    <SkeletonLoader preset="paragraph" :loading="true" class="h-20" />
                    <div class="flex justify-end">
                        <SkeletonLoader preset="button" :loading="true" class="w-16 h-8" />
                    </div>
                </div>
                <div v-else class="space-y-3">
                    <AutosizeTextarea
                        id="transformObjective"
                        v-model="transformObjective"
                        :min-height="80"
                        :max-height="200"
                        placeholder="Why create this transform? What problem does it solve? What is the expected outcome?"
                        class="resize-none" />
                    <div class="flex justify-end">
                        <LoadingButton
                            @click="saveTransformObjective"
                            :is-loading="savingObjective"
                            :disabled="!hasObjectiveChanges"
                            size="sm"
                            class="bg-primary hover:bg-primary">
                            <Settings class="w-4 h-4 mr-2" />
                            Save
                        </LoadingButton>
                    </div>
                </div>
            </CardContent>
        </Card>

        <!-- 2 & 3. Language-Specific Transform Components -->
        <div v-if="loadingPage">
            <!-- Skeleton for Input/Output Topics section -->
            <Card class="transition-opacity">
                <CardHeader>
                    <CardTitle class="text-xl"
                        ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">2</Badge>Input / Output Topics</CardTitle
                    >
                    <CardDescription> Define patterns and output topics via the topics page. </CardDescription>
                </CardHeader>
                <CardContent>
                    <div class="grid grid-cols-1 md:[grid-template-columns:1fr_1fr_auto] gap-4 w-full">
                        <div class="w-full space-y-2">
                            <SkeletonLoader preset="label" :loading="true" class="h-4 w-32" />
                            <SkeletonLoader preset="input" :loading="true" class="h-10" />
                        </div>
                        <div class="w-full space-y-2">
                            <SkeletonLoader preset="label" :loading="true" class="h-4 w-40" />
                            <SkeletonLoader preset="input" :loading="true" class="h-10" />
                        </div>
                        <div class="flex items-end space-x-3">
                            <SkeletonLoader preset="button" :loading="true" class="w-48 h-10" />
                        </div>
                    </div>
                    <div class="table-page-container mt-10 mb-5">
                        <div class="table-page-body">
                            <div class="w-full border rounded-md">
                                <div class="p-4 space-y-3">
                                    <div class="flex justify-between">
                                        <SkeletonLoader preset="table-header" :loading="true" class="h-6 w-24" />
                                        <SkeletonLoader preset="table-header" :loading="true" class="h-6 w-40" />
                                    </div>
                                    <div v-for="n in 5" :key="`skeleton-row-${n}`" class="flex justify-between">
                                        <SkeletonLoader preset="table-name" :loading="true" class="h-6 w-64" />
                                        <SkeletonLoader preset="table-name" :loading="true" class="h-6 w-48" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <!-- Skeleton for Transform Logic section -->
            <Card class="transition-opacity">
                <CardHeader>
                    <CardTitle class="text-xl"
                        ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">3</Badge>Transform Logic</CardTitle
                    >
                    <CardDescription>Loading transform logic...</CardDescription>
                </CardHeader>
                <CardContent>
                    <div class="bg-muted/5 border-l-4 border-border p-4 mb-4 space-y-4">
                        <div class="space-y-3">
                            <SkeletonLoader preset="paragraph" :loading="true" class="h-6" />
                            <SkeletonLoader preset="button" :loading="true" class="w-32 h-8" />
                        </div>
                        <div class="space-y-3">
                            <SkeletonLoader preset="paragraph" :loading="true" class="h-6" />
                            <SkeletonLoader preset="button" :loading="true" class="w-28 h-8" />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>

        <JavaScriptTransform
            v-else-if="currentLanguage === TRANSFORM_LANGUAGE_ENUM.JAVASCRIPT && transform"
            :transform="transform"
            :implementationCurrentVersion="implementationCurrentVersion"
            :hasUnsavedChanges="hasUnsavedChanges"
            :isSaving="isSaving"
            :saveError="saveError"
            @implementation-updated="handleImplementationUpdate"
            @implementation-synced="handleImplementationSync"
            @show-example-clicked="handleShowExampleClick" />

        <PythonTransform
            v-else-if="currentLanguage === TRANSFORM_LANGUAGE_ENUM.PYTHON && transform"
            :transform="transform"
            :implementationCurrentVersion="implementationCurrentVersion"
            :hasUnsavedChanges="hasUnsavedChanges"
            :isSaving="isSaving"
            :saveError="saveError"
            @implementation-updated="handleImplementationUpdate"
            @implementation-synced="handleImplementationSync"
            @show-example-clicked="handleShowExampleClick" />

        <SqlTransform
            v-else-if="currentLanguage === TRANSFORM_LANGUAGE_ENUM.SQL && transform"
            :transform="transform"
            :implementationCurrentVersion="implementationCurrentVersion"
            :hasUnsavedChanges="hasUnsavedChanges"
            :isSaving="isSaving"
            :saveError="saveError"
            @implementation-updated="handleImplementationUpdate"
            @implementation-synced="handleImplementationSync"
            @show-example-clicked="handleShowExampleClick" />

        <!-- 4. Deploy & Check -->
        <Card>
            <CardHeader>
                <CardTitle class="text-xl"
                    ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">4</Badge>Deploy &amp; Check</CardTitle
                >
                <CardDescription>Deploy your transform and validate its operation</CardDescription>
            </CardHeader>
            <CardContent class="pt-0">
                <div v-if="loadingPage" class="space-y-4">
                    <div v-for="n in 5" :key="`deploy-skeleton-${n}`" class="border rounded-md p-4">
                        <div class="flex items-center gap-3 mb-3">
                            <SkeletonLoader preset="badge" :loading="true" class="w-8 h-8 rounded-full" />
                            <SkeletonLoader preset="label" :loading="true" class="h-6 w-32" />
                        </div>
                        <div class="space-y-2 pl-11">
                            <SkeletonLoader preset="paragraph" :loading="true" class="h-4" />
                            <div class="flex items-center space-x-3">
                                <SkeletonLoader preset="input" :loading="true" class="h-10 w-40" />
                                <SkeletonLoader preset="button" :loading="true" class="h-10 w-32" />
                            </div>
                        </div>
                    </div>
                </div>
                <Accordion v-else type="multiple" collapsible class="w-full" v-model="activeAccordionItems">
                    <!-- Clear Topics Step -->
                    <AccordionItem value="clear-topics">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge variant="outline" class="w-8 h-8 rounded-full p-0 justify-center">1</Badge>
                                    <div
                                        v-if="hasClearTopicsError"
                                        class="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full border-2 border-background"></div>
                                </div>
                                <span>Clear Output Topics</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <div class="flex flex-col space-y-3">
                                    <Label class="text-sm font-medium mb-2 block">Topic Pattern to Clear</Label>
                                    <div class="flex items-center space-x-3">
                                        <Input
                                            v-model="outputTopicPatternToClear"
                                            placeholder="e.g. .*"
                                            class="flex-1"
                                            :disabled="loadingClearOutputTopics" />
                                        <LoadingButton
                                            @click="callClearOutputTopicsForTransform"
                                            :is-loading="loadingClearOutputTopics"
                                            variant="destructive"
                                            class="shrink-0">
                                            <Trash2 class="w-4 h-4 mr-2" />
                                            Clear Topics & State
                                        </LoadingButton>
                                    </div>
                                </div>

                                <Alert variant="destructive">
                                    <AlertTriangle class="h-4 w-4" />
                                    <AlertTitle>Warning</AlertTitle>
                                    <AlertDescription>
                                        This will permanently clear all output topics matching the pattern. Use with caution.
                                    </AlertDescription>
                                </Alert>

                                <Alert v-if="clearOutputTopicsResult" variant="default" class="border-green-200 bg-green-50">
                                    <CheckCircle class="h-4 w-4 text-green-600" />
                                    <AlertDescription class="text-green-800">{{ clearOutputTopicsResult }}</AlertDescription>
                                </Alert>

                                <ErrorMessage
                                    v-if="clearOutputTopicsError"
                                    :error="clearOutputTopicsError"
                                    :to-sentry="true"
                                    context="transform-impl-clear-output-topics" />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <!-- Deploy Step -->
                    <AccordionItem value="deploy">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge
                                        variant="outline"
                                        class="w-8 h-8 rounded-full p-0 justify-center relative"
                                        :class="{
                                            'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400':
                                                stepStatus.deploy === 'success',
                                            'border-destructive bg-destructive/10 text-destructive dark:bg-destructive/20':
                                                stepStatus.deploy === 'error'
                                        }">
                                        <LoaderCircle v-if="stepStatus.deploy === 'loading'" class="h-4 w-4 animate-spin" />
                                        <span v-else>2</span>
                                    </Badge>
                                </div>
                                <span>Deploy Transform</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <div class="flex flex-col space-y-3">
                                    <Label class="text-sm font-medium mb-2 block">Replay Window</Label>
                                    <p class="text-sm text-muted-foreground">
                                        Optional time window for replaying events (e.g. 3h, 2d, 10m). Leave empty or use 0 to stream from last
                                        processed message.
                                    </p>
                                    <div class="flex items-center space-x-3">
                                        <Input v-model="replayWindow" placeholder="24h" class="w-32" :disabled="loadingDeployLive" />
                                        <LoadingButton
                                            @click="callTriggerDeployLive"
                                            :is-loading="loadingDeployLive"
                                            :disabled="!transform?.id"
                                            class="shrink-0">
                                            <Rocket class="w-4 h-4 mr-2" />
                                            Deploy Transform
                                        </LoadingButton>
                                    </div>
                                </div>

                                <Alert v-if="deployLiveResult" variant="default" class="border-green-200 bg-green-50">
                                    <CheckCircle class="h-4 w-4 text-green-600" />
                                    <AlertDescription class="text-green-800">{{ deployLiveResult }}</AlertDescription>
                                </Alert>

                                <ErrorMessage v-if="deployError" :error="deployError" />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <!-- Check Job Status Step -->
                    <AccordionItem value="job-status">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge
                                        variant="outline"
                                        class="w-8 h-8 rounded-full p-0 justify-center relative"
                                        :class="{
                                            'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400':
                                                stepStatus.jobStatus === 'success',
                                            'border-destructive bg-destructive/10 text-destructive dark:bg-destructive/20':
                                                stepStatus.jobStatus === 'error'
                                        }">
                                        <LoaderCircle v-if="stepStatus.jobStatus === 'loading'" class="h-4 w-4 animate-spin" />
                                        <span v-else>3</span>
                                    </Badge>
                                </div>
                                <span>Check Job Status</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <LoadingButton @click="callCheckJobStatus" :is-loading="loadingCheckJobStatus" variant="outline">
                                    <Activity class="w-4 h-4 mr-2" />
                                    Check Job Status
                                </LoadingButton>

                                <Alert v-if="jobStatus" variant="default" class="border-blue-200 bg-blue-50">
                                    <CheckCircle class="h-4 w-4 text-blue-600" />
                                    <AlertDescription class="text-blue-800">{{ jobStatus }}</AlertDescription>
                                </Alert>

                                <ErrorMessage v-if="jobStatusError" :error="jobStatusError" />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <!-- Check Output Topics Step -->
                    <AccordionItem value="output-topics">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge
                                        variant="outline"
                                        class="w-8 h-8 rounded-full p-0 justify-center relative"
                                        :class="{
                                            'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400':
                                                stepStatus.outputTopics === 'success',
                                            'border-destructive bg-destructive/10 text-destructive dark:bg-destructive/20':
                                                stepStatus.outputTopics === 'error'
                                        }">
                                        <LoaderCircle v-if="stepStatus.outputTopics === 'loading'" class="h-4 w-4 animate-spin" />
                                        <span v-else>4</span>
                                    </Badge>
                                </div>
                                <span>Check Output Topics</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <LoadingButton @click="callCheckOutputTopicsForTransform" :is-loading="loadingCheckOutputTopics" variant="outline">
                                    <Database class="w-4 h-4 mr-2" />
                                    Check Output Topics
                                </LoadingButton>

                                <Alert
                                    v-if="!loadingCheckOutputTopics && hasCheckedOutputTopics && checkOutputTopics.length > 0"
                                    variant="default"
                                    class="border-green-200 bg-green-50">
                                    <CheckCircle class="h-4 w-4 text-green-600" />
                                    <AlertDescription class="text-green-800">Found {{ checkOutputTopics.length }} output topics.</AlertDescription>
                                </Alert>

                                <Alert
                                    v-else-if="!loadingCheckOutputTopics && hasCheckedOutputTopics"
                                    variant="default"
                                    class="border-yellow-200 bg-yellow-50">
                                    <AlertTriangle class="h-4 w-4 text-yellow-600" />
                                    <AlertDescription class="text-yellow-800">No output topics found.</AlertDescription>
                                </Alert>

                                <!-- Output Topics Table -->
                                <div v-if="hasCheckedOutputTopics" class="mt-4">
                                    <div class="overflow-x-auto overflow-y-auto max-h-96 border rounded-lg">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead
                                                        class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                                        Topic
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                <TableRow v-for="outTopic in checkOutputTopics" :key="outTopic" class="hover:bg-muted/50">
                                                    <TableCell class="px-6 py-4 whitespace-nowrap cursor-pointer font-medium text-primary">
                                                        <div class="flex flex-col items-start gap-1">
                                                            <div class="my-auto whitespace-normal break-words hover:text-primary/80">
                                                                <a :href="`/topics/${outTopic}`" target="_blank" class="no-underline hover:underline">
                                                                    {{ outTopic.replace(/(source_|transform_)[^.]+\./, '') }}
                                                                </a>
                                                            </div>
                                                            <small class="font-normal text-muted-foreground">ID: {{ outTopic }}</small>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                            <TableEmpty v-if="!checkOutputTopics.length" :colspan="1">
                                                <TableEmptyState />
                                            </TableEmpty>
                                        </Table>
                                    </div>
                                </div>

                                <ErrorMessage v-if="checkOutputTopicsError" :error="checkOutputTopicsError" />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <!-- Check Flink Errors Step -->
                    <AccordionItem value="check-flink-errors">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge
                                        variant="outline"
                                        class="w-8 h-8 rounded-full p-0 justify-center relative"
                                        :class="{
                                            'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400':
                                                stepStatus.checkFlinkErrors === 'success',
                                            'border-destructive bg-destructive/10 text-destructive dark:bg-destructive/20':
                                                stepStatus.checkFlinkErrors === 'error'
                                        }">
                                        <LoaderCircle v-if="stepStatus.checkFlinkErrors === 'loading'" class="h-4 w-4 animate-spin" />
                                        <span v-else>5</span>
                                    </Badge>
                                </div>
                                <span>Check Flink Exceptions</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <LoadingButton @click="callCheckErrorsForTransform" :is-loading="loadingCheckErrors" variant="outline">
                                    <AlertTriangle class="w-4 h-4 mr-2" />
                                    Check Flink Exceptions
                                </LoadingButton>

                                <div v-if="!loadingCheckErrors && hasCheckedErrors && checkErrors.length > 0" class="space-y-3">
                                    <div v-for="(err, index) in checkErrors" :key="`error-${index}-${err.job_id || err.message}`" class="space-y-2">
                                        <!-- Job Info (if available and different from previous error) -->
                                        <div
                                            v-if="
                                                (err.job_id || err.job_name) &&
                                                (index === 0 ||
                                                    err.job_id !== checkErrors[index - 1]?.job_id ||
                                                    err.job_name !== checkErrors[index - 1]?.job_name)
                                            "
                                            class="flex items-center gap-3 text-sm border-b border-border pb-1 mb-2">
                                            <div v-if="err.job_id" class="flex items-center gap-2">
                                                <span class="text-muted-foreground">Job ID:</span>
                                                <Badge variant="outline" class="text-xs font-mono">{{ err.job_id }}</Badge>
                                            </div>
                                            <div v-if="err.job_name" class="flex items-center gap-2">
                                                <span class="text-muted-foreground">Job Name:</span>
                                                <code class="text-xs bg-muted px-2 py-1 rounded">{{ err.job_name }}</code>
                                            </div>
                                        </div>

                                        <!-- Error Message with visual grouping -->
                                        <div class="border-l-2 border-destructive pl-3">
                                            <ErrorMessage :error="err.message + (err.stacktrace ? '\n' + err.stacktrace : '')" />
                                        </div>
                                    </div>
                                </div>

                                <Alert v-else-if="!loadingCheckErrors && hasCheckedErrors" variant="default" class="border-green-200 bg-green-50">
                                    <CheckCircle class="h-4 w-4 text-green-600" />
                                    <AlertDescription class="text-green-800">No Flink exceptions found.</AlertDescription>
                                </Alert>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <!-- Check DLQ Errors Step -->
                    <AccordionItem value="check-dlq-errors">
                        <AccordionTrigger class="hover:no-underline">
                            <div class="flex items-center gap-3">
                                <div class="relative">
                                    <Badge
                                        variant="outline"
                                        class="w-8 h-8 rounded-full p-0 justify-center relative"
                                        :class="{
                                            'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400':
                                                stepStatus.checkDlqErrors === 'success',
                                            'border-destructive bg-destructive/10 text-destructive dark:bg-destructive/20':
                                                stepStatus.checkDlqErrors === 'error'
                                        }">
                                        <LoaderCircle v-if="stepStatus.checkDlqErrors === 'loading'" class="h-4 w-4 animate-spin" />
                                        <span v-else>6</span>
                                    </Badge>
                                </div>
                                <span>Check Dead Letter Queue Errors</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent class="pb-6">
                            <div class="space-y-4">
                                <LoadingButton @click="callCheckDlqErrorsForTransform" :is-loading="loadingCheckDlqErrors" variant="outline">
                                    <Database class="w-4 h-4 mr-2" />
                                    Check DLQ Errors
                                </LoadingButton>

                                <DlqErrorDisplay
                                    :dlq-error="currentDlqError"
                                    :loading="currentDlqErrorLoading"
                                    :error="currentDlqErrorError"
                                    :show-no-errors-message="hasCheckedDlqErrors && !currentDlqError" />
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </CardContent>
        </Card>
    </div>
</template>

<script setup>
import { TRANSFORM_LANGUAGE_ENUM } from '../../utils/constants';
import JavaScriptTransform from './PerLanguage/JavaScriptTransform.vue';
import PythonTransform from './PerLanguage/PythonTransform.vue';
import SqlTransform from './PerLanguage/SqlTransform.vue';

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { LoadingButton } from '@/components/ui/loading-button';
import { AutosizeTextarea } from '@/components/ui/autosize-textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Settings, AlertTriangle, CheckCircle, Trash2, Rocket, Activity, Database, LoaderCircle } from 'lucide-vue-next';
import ErrorMessage from '@/components/ErrorMessage.vue';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import DlqErrorDisplay from '@/components/DlqErrorDisplay.vue';
import { useTransformsStore } from '@/stores/transforms.store';
import { useRouter } from 'vue-router';

import { useTransformSaveOrchestrator } from '@/composables/useTransformSaveOrchestrator';

// Stores and router
const transformsStore = useTransformsStore();
const router = useRouter();

// Props from route
const props = defineProps({
    transformId: {
        type: String,
        required: true
    }
});

const {
    // Loading state
    isLoading: loadingPage,
    loadError,

    // Data state
    transform,
    implementationHistory,
    transformObjective,
    originalObjective,
    implementationCurrentVersion,

    // Save state
    isSaving,
    saveError,
    hasUnsavedChanges,
    savingObjective,

    // Methods
    loadTransformData,
    saveTransformObjective,
    saveImplementation,
    handleContentChange,
    handleShowExample,
    handleManualSave,
    initializeImplementation,
    clearSaveError,
    configureAutoSave
} = useTransformSaveOrchestrator(props.transformId);

const pollingInterval = ref(null);

const stopPolling = () => {
    if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
    }
};

const pollAllData = async () => {
    try {
        // Step 1: Always check job status first
        const jobStatusData = await transformsStore.getJobStatus(props.transformId, false);

        // Step 2: Only proceed with other calls if job is "running"
        if (jobStatusData?.status?.toLowerCase() === 'running') {
            // Call remaining APIs silently in parallel
            await Promise.allSettled([
                callCheckOutputTopicsForTransform(false),
                callCheckErrorsForTransform(false),
                callCheckDlqErrorsForTransform(false)
            ]);
        }
    } catch (error) {
        // Ignore errors - polling should be resilient
        console.error('Polling error:', error);
    }
};

// Step 4 (Deploy & Check) state
const outputTopicPatternToClear = ref('.*');
const clearOutputTopicsResult = ref('');
const clearOutputTopicsError = ref(null);
const loadingClearOutputTopics = ref(false);

const deployLiveResult = ref('');
const deployError = ref(null);
const loadingDeployLive = ref(false);

const jobStatus = ref(false);
const jobStatusError = ref(null);
const loadingCheckJobStatus = ref(false);

const checkOutputTopics = ref([]);
const checkOutputTopicsError = ref(null);
const loadingCheckOutputTopics = ref(false);
const hasCheckedOutputTopics = ref(false);

const checkErrors = ref([]);
const loadingCheckErrors = ref(false);
const hasCheckedErrors = ref(false);

const checkDlqErrors = ref(null);
const loadingCheckDlqErrors = ref(false);
const hasCheckedDlqErrors = ref(false);
const checkDlqErrorsError = ref(null);

const replayWindow = ref('');

// Accordion control and visual feedback - default to expanded
const activeAccordionItems = ref(['clear-topics', 'deploy', 'job-status', 'output-topics', 'check-flink-errors', 'check-dlq-errors']);
const stepStatus = ref({
    deploy: 'idle', // idle, loading, success, error
    jobStatus: 'idle',
    outputTopics: 'idle',
    checkFlinkErrors: 'idle',
    checkDlqErrors: 'idle'
});

// Computed property to determine current language
const currentLanguage = computed(() => {
    return (
        implementationCurrentVersion.value?.implementation?.language?.toUpperCase() ||
        transform.value?.config['transforms.language']?.toUpperCase() ||
        null
    );
});

// Computed property to detect objective changes
const hasObjectiveChanges = computed(() => {
    return transformObjective.value !== originalObjective.value;
});

// Computed properties to detect errors in accordion steps
const hasClearTopicsError = computed(() => {
    return !!clearOutputTopicsError.value;
});

// Simplified - just use the manual check results for display
const currentDlqError = computed(() => checkDlqErrors.value);
const currentDlqErrorLoading = computed(() => loadingCheckDlqErrors.value);
const currentDlqErrorError = computed(() => checkDlqErrorsError.value);

// Load transform and implementation data - now delegated to composable
async function loadTransform() {
    try {
        await loadTransformData();
    } catch (error) {
        console.error('Error loading transform:', error);
        router.replace({ name: '404', params: { customMessage: 'Transform not found' } });
    }
}

// Constants
const NO_VERSION = '0';

// Deploy & Check functions (Step 4)
const callTriggerDeployLive = async () => {
    loadingDeployLive.value = true;
    deployError.value = null;
    deployLiveResult.value = '';

    // Reset all step statuses
    stepStatus.value.deploy = 'loading';
    stepStatus.value.jobStatus = 'idle';
    stepStatus.value.outputTopics = 'idle';
    stepStatus.value.checkFlinkErrors = 'idle';
    stepStatus.value.checkDlqErrors = 'idle';

    const transformId = transform.value.id;
    const startTime = Date.now();

    try {
        await transformsStore.deployPreview(transformId, NO_VERSION, replayWindow.value);
        await transformsStore.deployLive(transformId, NO_VERSION);
        const endTime = Date.now();
        const durationSec = Math.floor((endTime - startTime) / 1000);
        deployLiveResult.value = `Successfully deployed transform live in ${durationSec} sec.`;

        stepStatus.value.deploy = 'success';

        // Automatically run the check steps after successful deployment with visual feedback
        // Add small delays for better visual flow
        await new Promise((resolve) => setTimeout(resolve, 500));
        await callCheckJobStatus(true);

        await new Promise((resolve) => setTimeout(resolve, 300));
        await callCheckOutputTopicsForTransform(true);

        await new Promise((resolve) => setTimeout(resolve, 300));
        await callCheckErrorsForTransform(true);

        await new Promise((resolve) => setTimeout(resolve, 300));
        await callCheckDlqErrorsForTransform(true);
    } catch (e) {
        console.error('Error deploying live', e);
        deployError.value = e;
        stepStatus.value.deploy = 'error';
    } finally {
        loadingDeployLive.value = false;
    }
};

const callCheckJobStatus = async (showLoading = false) => {
    if (showLoading) {
        loadingCheckJobStatus.value = true;
        jobStatus.value = '';
        jobStatusError.value = null;

        stepStatus.value.jobStatus = 'loading';
    }

    try {
        const data = await transformsStore.getJobStatus(transform.value.id, showLoading);
        jobStatus.value = `Job Status: ${data.status || 'Unknown'}`;

        if (showLoading) {
            stepStatus.value.jobStatus = 'success';
        }
    } catch (e) {
        console.error('Error checking job status', e);
        jobStatusError.value = e;

        if (showLoading) {
            stepStatus.value.jobStatus = 'error';
        }
    } finally {
        loadingCheckJobStatus.value = false;
    }
};

const callCheckOutputTopicsForTransform = async (showLoading = false) => {
    if (showLoading) {
        loadingCheckOutputTopics.value = true;
        checkOutputTopics.value = [];
        checkOutputTopicsError.value = null;

        stepStatus.value.outputTopics = 'loading';
    }

    try {
        const data = await transformsStore.getOutputTopicsForTransform(transform.value.id, showLoading);
        checkOutputTopics.value = data || [];
        hasCheckedOutputTopics.value = true;

        if (showLoading) {
            stepStatus.value.outputTopics = 'success';
        }
    } catch (e) {
        console.error('Error checking output topics', e);
        checkOutputTopicsError.value = e;

        if (showLoading) {
            stepStatus.value.outputTopics = 'error';
        }
    } finally {
        loadingCheckOutputTopics.value = false;
    }
};

const callCheckErrorsForTransform = async (showLoading = false) => {
    if (showLoading) {
        loadingCheckErrors.value = true;
        checkErrors.value = [];

        stepStatus.value.checkFlinkErrors = 'loading';
    }

    try {
        const data = await transformsStore.getErrorsForTransform(transform.value.id, showLoading);
        checkErrors.value = data || [];
        hasCheckedErrors.value = true;

        if (showLoading) {
            // Original WithVisuals logic: 'error' if errors found, 'success' if none
            stepStatus.value.checkFlinkErrors = checkErrors.value?.length > 0 ? 'error' : 'success';
        }
    } catch (e) {
        console.error('Error checking errors', e);
        checkErrors.value = [{ message: 'Error checking for errors', stacktrace: e.toString() }];

        if (showLoading) {
            stepStatus.value.checkFlinkErrors = 'error';
        }
    } finally {
        loadingCheckErrors.value = false;
    }
};

const callCheckDlqErrorsForTransform = async (showLoading = false) => {
    if (showLoading) {
        loadingCheckDlqErrors.value = true;
        checkDlqErrors.value = null;
        checkDlqErrorsError.value = null;

        stepStatus.value.checkDlqErrors = 'loading';
    }

    try {
        const data = await transformsStore.getDlqErrorsForTransform(transform.value.id, showLoading);
        checkDlqErrors.value = data;
        hasCheckedDlqErrors.value = true;

        if (showLoading) {
            stepStatus.value.checkDlqErrors = 'success';
        }
    } catch (e) {
        console.error('Error checking DLQ errors', e);
        checkDlqErrorsError.value = e;

        if (showLoading) {
            stepStatus.value.checkDlqErrors = 'error';
        }
    } finally {
        loadingCheckDlqErrors.value = false;
    }
};

const callClearOutputTopicsForTransform = async () => {
    loadingClearOutputTopics.value = true;
    clearOutputTopicsResult.value = '';
    clearOutputTopicsError.value = null;

    try {
        const startTime = new Date().getTime();
        await transformsStore.clearOutputTopics(transform.value.id, outputTopicPatternToClear.value);
        const endTime = new Date().getTime();
        if (!replayWindow.value) {
            // replayWindow needs to be defined, otherwise the previous consumer group offsets will be used and Clear topics will have not effect
            replayWindow.value = '7d';
        }
        const durationSec = Math.floor((endTime - startTime) / 1000);
        clearOutputTopicsResult.value = `Successfully cleared output topics matching pattern: ${outputTopicPatternToClear.value} in ${durationSec} sec.`;
    } catch (e) {
        console.error('Error clearing output topics', e);
        clearOutputTopicsError.value = e;
    } finally {
        loadingClearOutputTopics.value = false;
    }
};

// UNIFIED: Single implementation update handler (replaces dual paths)
const handleImplementationUpdate = (updatedImplementation) => {
    handleContentChange(updatedImplementation);
};

// Handle synced implementations (already saved, just update local state)
const handleImplementationSync = (updatedImplementation) => {
    // Update local state without triggering another save since data was already saved
    implementationCurrentVersion.value = updatedImplementation;
};
// UNIFIED: Handle "show example" clicks from child components
const handleShowExampleClick = (data) => {
    // Use the save orchestrator's handleShowExample for proper state management
    const updatedImplementation = { ...implementationCurrentVersion.value.implementation };

    // Update the specific field based on tabId
    switch (data.tabId) {
        case 'COMMON_TRANSFORM':
            updatedImplementation.common_transform = data.template;
            break;
        case 'VALUE_TRANSFORM':
            updatedImplementation.value_transform = data.template;
            break;
        case 'KEY_TRANSFORM':
            updatedImplementation.key_transform = data.template;
            break;
        case 'TOPIC_TRANSFORM':
            updatedImplementation.topic_transform = data.template;
            break;
        case 'VALUE_SCHEMA_TRANSFORM':
            updatedImplementation.value_schema_transform = data.template;
            break;
        case 'KEY_SCHEMA_TRANSFORM':
            updatedImplementation.key_schema_transform = data.template;
            break;
    }

    handleShowExample(updatedImplementation);
};

// Handle implementation updates from child components

// Initialize on mount
onMounted(async () => {
    await loadTransform();
    // Configure auto-save (enabled with 2-second debounce)
    configureAutoSave(true, 2000);

    // Check if polling should start (page refresh scenario)

    // Then every 3 seconds
    pollingInterval.value = setInterval(pollAllData, 3000);
});

// Cleanup on component destroy
onUnmounted(() => {
    stopPolling();
});
</script>

<style scoped>
.editor-backdrop {
    background-color: rgba(0, 0, 0, 0.3);
}
</style>
